/**
 * 再現エンジンの入力画面（Issue #467）。`/duels/:duelsId/game/:gameUuid/input`
 *
 * **スマホの縦画面が基準**。これまで（/ocg → /play → /arena。どれも削除した）は動画の再生・core への応答・カードの登録を 1 画面の盤のタップに
 * 同居させて特例が増え続けたので、入力を持つ仕組みを 1 度に 1 つにする:
 *   上半分（StageView）: フィールド動画 + core の盤面 + ゾーンの枠。**表示だけ**（盤は押さない）。表示の切り替え（全体 / フィールド / 手札）
 *   下半分の上端（InfoStrip）: 応答のあとに起きたイベントを TICKER_MS ずつ順に出す 1 行と、今の時刻の発話の 1 行（上半分の動画を文字で隠さない）
 *   下半分: タブで「再生」「応答」「登録」「発話」を切り替える。応答・登録に切り替えたら動画を止める（戻っても自動では再生しない）。
 *           発話タブ（SpeechTab）は発話区間のレビュー（採否・文の直し・LLM の提案とレビュー。Issue #998 でシミュレータの画面から移した）。動画は止めない（聞き直す）。席モードには出さない。
 *           応答の時刻は止めた位置の動画の時刻。再生タブに先攻・開始位置と「全部消してやり直す」（replay を消して作り直す。
 *           操作・登録・演出エディタのタイミングは replay に紐づく子テーブルなので一緒に消える）。
 *           登録タブは起きた順の 1 行 1 件の一覧（RegisterList。既定は未確認だけ）。ドローの選び直しは横長のデッキ一覧（ocgInput/RegisterPanel）で、
 *           選ぶか OK で確定したら同じプレイヤーの次の未確認のドローを開く（初手を続けて登録できる）。コイン・サイコロ・ランダム選択は RegisterPickDialog
 * **デュエル開始前**（開始位置が未設定・動画がそれより前。PreStartPanel.isBeforeStart）は core の盤面・問い合わせ・登録を出さず、自動でも答えない。
 *           開始位置を決めた瞬間に、開始時のイベント（初手のドローなど）を情報帯に流す
 * **未登録のカード（ドロー・コイン・サイコロ・ランダム選択）が出たイベントで止める**（Issue #612。登録待ち）: ocgCore の呼び出しに `hold` を付け、サービスが最後の応答より
 *           後ろで未確認のカードを使った step で止めて `hold` を返す（問い合わせは無い）。応答タブに項目と「登録する」を出し、登録（確定）すると次の未登録か問い合わせまで進む。
 *           確定したら次の登録待ちがあれば続けて開く。core を一時停止しているのではなく、毎回頭から再実行して「そこまで」で切っている
 * **チェーン確認を自動で通すか・登録だけにするか・戻した直後に止めるかは replay の入力の決まり**（Issue #668。サーバーの `input_policy`。登録タブの「入力の決まり」で変える）:
 *           **チェーン確認を通すのは本番だけ**（Issue #927）: 人の答え・登録・開始などの書き込みのあと、本番が `query.auto` の確認を「チェーンしない」で
 *           印（ops[].auto）付きで書いて続けて通す。画面は送らない（前は `auto` の印を見て `asAuto` を送っていた）。「戻す」・「戻って発動」の直後は本番も通さない。
 *           効果の発動確認・選択・止まる条件の場面は止まる。今の区間で通したチェーン確認のうち発動できたものは
 *           応答タブの「さっき通したところ（戻る）」（SkippedChances）に出し、選んで確定するとそこまで戻して（rewindTo）発動する。
 *           「戻す」は最後の人の答えまで消す（undoCountToLastHuman。1 手ずつだと自動で通した確認に戻ってまた通すだけになる）。
 * **最後の応答から今の問い合わせまでに未確認のカード（ドロー・コイン・サイコロ・ランダム選択）が出たら自動では通さない**（Issue #611。ocgCore の
 *           `unconfirmedSinceLastAnswer`）—— core は仮のカードで「発動できるカードが無い」と言っているだけ。登録タブで確定するか、そのまま答えれば先へ進む。
 *
 * **PC（横長で幅 1000px 以上）は並べ方だけ変える**（Issue #470。inputLayout.js。ページ・状態・部品は同じ）: 左に上半分の盤面を高さいっぱい、
 * その下に情報帯と常時出す再生バー（TransportBar。再生タブは要らない）、右に 応答 / 登録（幅 1500px 以上は両方並べる）。
 * 「応答・登録に切り替えたら動画を止める」は「右のパネルを押したら動画を止める」に読み替える。キーボードの操作は pcKeys.js の表。
 * **盤面（StageView）は並べ方が変わってもツリーの同じ位置（key="stage"）に置く** —— 位置が変わると動画が作り直されて再生位置が頭に戻る。
 * **`?live=1&rec=<録画の鍵>`: 補正サーバー（EC2）の録画**（Issue #617 / #627）。補正サーバーが同じ origin（CloudFront の /live/*）で配る
 * `/live/stream?rec=` を、フィールド動画の `<video>` の代わりに使う（liveVideo.js。`<video>` と同じ顔なので時刻の読み書きはそのまま）。
 * 時刻は録画の開始からの秒＝あとで作るフィールド動画と同じ。ゾーンは補正サーバーの `/live/info.json` の overlay_zones（配っている絵そのもの）。
 * **rec の無い `?live=1` は今の対戦の録画を追う**（Issue #744。撮影予定の「P1 / P2 / 観客として参加」）: GetLiveMatch（席モードは MatchPanel が読んだもの）から
 * この試合の時計の録画の鍵（本番が選ぶ `clock`。席によらない。utils/liveMatch.js の liveRecordingKey。Issue #803）が、付いたら `?rec=` へ、付く前は `/live/stream?preview=1`（補正サーバーの今の絵。
 * ライブの端だけで巻き戻せない）。preview の時刻は録画の時刻ではないので、応答に video_ms を付けない（サーバーが「今 − 録画を始めた時間」で埋める）・
 * 開始前の判定に映像として数えない・シークしない。録画し直して鍵が変わればまた切り替える（対戦が終わっても最後の鍵のまま）。
 * **`?watch=1`: 見る専用**（Issue #617）。盤面（上半分）を画面いっぱいに出し、応答・登録・戻す・自動で通すを**全部出さない / しない**（何も書き込まない）。
 * 入力している端末の操作は WATCH_POLL_MS ごとに取り直して盤面と情報帯に反映する。複数の端末で同時に入力すると古い問い合わせに答えてしまうので、
 * 入力は 1 台に決めて残りはこれで見る。ルーム（ゲームサーバー）が開いているかは席なしの OcgSeatState で聞く（Issue #761）。**`?view=all|field|hand0|hand1`** は最初の表示（端末ごとに「P1 の手札を映す」と決めて置ける）。
 * **デッキレシピの取り込みと復旧**（Issue #618）: replay のデッキは作った時点の写しなので、開いたときに試合のデッキの今のレシピを取り込む
 * （OcgSyncDeck。記録済みの応答は選んだカードで付け直して確かめる。見る専用では呼ばない）。登録タブにも「デッキレシピを取り直す」。
 * 再実行が止まったら RecoveryPanel（理由・その手へ・自動で付け直す・ここから入力し直す）。
 * **問い合わせの読み方と答え方はサーバー**（Issue #663 / #665）: 状態に付く `input`（今答える問い合わせ・選択肢・登録の一覧・日本語の文。
 * server/synchro_server/ocg/input_view.py）をそのまま描き、答えは ocgCore の `answer`（OcgAnswer。選んだ選択肢を送るだけ。payload は組まない）。
 * 押したもの → 答えの対応付けは inputView.js（形の道具）。
 * **ルーム（ゲームサーバー。Issue #765。索引 #761・docs/game-server.md）**: 席モードと見る専用は、本番の OcgSeatState の返りに `room` が付いていれば
 * ルームの WebSocket へつなぎ替えて本番のポーリングを止める（ocgCore の RoomLink。判断は nativeBridge/roomLink.js の状態遷移表＝iPad と同じ）。
 * 答え・途中経過・戻す・登録・入力の決まり・飛ばす・追いつく・対戦を終えるはルームへ。切れたら「再接続中」を出して自分でつなぎ直し、
 * `closed` を受けたら・`room` が無くなったら今のポーリングに戻る。**`room` が無ければ（ルームが開いていない）今のまま何も変わらない**。
 * 見せ終わり待ち（state.wait）のあいだは問い合わせを出さず「演出を見せています」と「飛ばす」。「対戦を終える」はルームのメニューの中（決着では自動で閉じない）。
 * 見え方に「演出」（ライブの録画があるとき。補正サーバーの `/live/stream?rec=…&fx=1`＝描く側のゲーム映像。盤面・ゾーンは重ねない）
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/api';
import { usePageTitle } from '../appInfo';
import { deckIdsOfGame, deckList, playersFromGame } from '../utils/gameInfo';
import { PlayerColorsProvider } from '../components/ocgInput/common';
import SkippedChances from '../components/ocgInput/SkippedChances';
import RegisterPanel from '../components/ocgInput/RegisterPanel';
import RegisterPickDialog from '../components/ocgInput/RegisterPickDialog';
import * as ocg from '../components/ocgCore';
import { addedTicker, queryAnswer, startTicker, TICKER_MS } from '../components/ocgInput/inputView';
import InputPolicyPanel from '../components/ocgInput/InputPolicyPanel';
import { nextSpeechMs, prevSpeechMs, speechIndexAt, subtitlesAt, useSpeechSegments } from '../components/ocgInput/speech';
import SpeechTab from '../components/ocgInput/SpeechTab';
import { toggleTargetAt } from '../utils/speechSegments';
import StageView from '../components/ocgInput/StageView';
import InfoStrip from '../components/ocgInput/InfoStrip';
import RespondTab, { btn } from '../components/ocgInput/RespondTab';
import PlaybackTab, { fmtTime } from '../components/ocgInput/PlaybackTab';
import RegisterList, { nextUnconfirmedDraw } from '../components/ocgInput/RegisterList';
import PreStartPanel, { isBeforeStart } from '../components/ocgInput/PreStartPanel';
import RpsPanel, { rpsResultText } from '../components/ocgInput/RpsPanel';
import { availableViews, resolveZones, VIEWS, zoneKeyOfPlaceKey } from '../components/ocgInput/stage';
import useInputLayout, { visiblePanels } from '../components/ocgInput/inputLayout';
import TransportBar from '../components/ocgInput/TransportBar';
import { actionOf } from '../components/ocgInput/pcKeys';
import LiveVideo, { liveStreamPath } from '../components/ocgInput/liveVideo';
import { fetchLiveInfo } from '../components/liveCorrect';
import WatchBar from '../components/ocgInput/WatchBar';
import RecoveryPanel from '../components/ocgInput/RecoveryPanel';
import { flipsView, helperOptions, isMyTurn, ownLocationText, progressPicks, seatChances, seatFromParam, stateForSeat } from '../components/ocgInput/seatView';
import MatchPanel, { MATCH_POLL_MS } from '../components/ocgInput/MatchPanel';
import { liveRecordingKey } from '../utils/liveMatch';
import { roomBody, roomOf } from '../nativeBridge/roomLink';

/** ticker に一度に積む上限（長いチェーンで何秒も待たせない。新しいほうを残す） */
const TICKER_MAX = 15;
/** 見る専用（?watch=1）で入力している端末の操作を取り直す間隔 */
export const WATCH_POLL_MS = 2000;
/** 席モード（?seat=1|2。タブレット。Issue #711）で相手の席の操作を取り直す間隔（版が同じなら 1 行読むだけ） */
export const SEAT_POLL_MS = 1000;
/** 登録待ちで止めるか・答えのあとに続けて通すかは replay の入力の決まり（サーバー。Issue #668）なので、画面からは何も付けない */
const HOLD = {};
const ANSWER = {};

/** この画面を開いている間はページ本体をスクロールさせない（一覧の端でページごと引っ張られない）。戻り値は元に戻す関数 */
function lockPageScroll() {
  const els = [document.documentElement, document.body];
  const before = els.map((el) => [el.style.overflow, el.style.overscrollBehavior]);
  els.forEach((el) => { el.style.overflow = 'hidden'; el.style.overscrollBehavior = 'none'; });
  return () => els.forEach((el, i) => { [el.style.overflow, el.style.overscrollBehavior] = before[i]; });
}

export const TABS = [
  { key: 'play', label: '再生' },
  { key: 'respond', label: '応答' },
  { key: 'register', label: '登録' },
  { key: 'speech', label: '発話' },
];
/** 席モードに出さないタブ（再生の操作と発話のレビューは対戦している人の仕事ではない） */
const SEAT_HIDDEN_TABS = new Set(['play', 'speech']);
/** このタブに切り替えたら動画を止めるか（入力するタブ） */
export const pausesVideo = (tab) => tab === 'respond' || tab === 'register';

export default function OcgInput() {
  const { duelsId, gameUuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?live=1: 補正サーバー（EC2。CloudFront の /live/*。Issue #626 / #627）の映像。?rec=<録画の鍵> があればその録画に固定、無ければ今の対戦の録画を追う（Issue #744）
  const recParam = searchParams.get('rec');
  const liveMode = searchParams.get('live') === '1';
  const followMatch = liveMode && !recParam;
  const watchMode = searchParams.get('watch') === '1';
  // 席モード（?seat=1|2。タブレット。Issue #711）: 席ごとの読み書き（OcgSeatState / 席つきの OcgAnswer）。見えないカードは裏、相手の番は答えない
  const mySeat = seatFromParam(searchParams.get('seat'));
  const seatMode = mySeat != null;
  const viewParam = searchParams.get('view');
  const [view, setView] = useState(() => (VIEWS.some((v) => v.key === viewParam) ? viewParam : 'all'));
  // 今の対戦の録画の鍵（followMatch のとき）。付いたら変わるまで持つ（対戦が終わっても preview に戻さない）
  const [matchKey, setMatchKey] = useState(null);
  const onMatch = useCallback((m) => {
    const key = liveRecordingKey(m, gameUuid);
    if (key) setMatchKey(key);
  }, [gameUuid]);
  // 席モードは MatchPanel が GetLiveMatch を読んでいるのでそれを受け取る。それ以外（観客・PC）はここで読む
  useEffect(() => {
    if (!followMatch || seatMode) return undefined;
    let alive = true;
    let rev = null;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const m = await api.project('GetLiveMatch', rev != null ? { since_rev: rev } : {});
        if (!alive || m?.changed === false) return;
        rev = m?.rev ?? null;
        onMatch(m);
      } catch (e) { /* 次の回に読み直す */ }
    };
    tick();
    const t = setInterval(tick, MATCH_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [followMatch, seatMode, onMatch]);
  const recKey = recParam || (followMatch ? matchKey : null);
  // 録画が付く前の補正サーバーの今の絵（時刻は録画の時刻ではない）
  const previewMode = liveMode && !recKey;
  // 見え方の「演出」（描く側のゲーム映像。Issue #765）: ライブの録画があるときだけ。URL に fx=1 を足すだけで、止める・シーク・ライブへ戻るはそのまま
  const fxAvailable = liveMode && !!recKey;
  const fxView = fxAvailable && view === 'fx';
  // 収録中のライブ映像（?live=1）。録画の鍵か演出の切り替えが変わったら作り直す
  const liveUrl = !liveMode ? null : liveStreamPath({ rec: recKey, fx: fxView });
  const liveVideo = useMemo(() => (liveUrl ? new LiveVideo({ url: liveUrl }) : null), [liveUrl]);
  const [liveZones, setLiveZones] = useState(null);
  const liveWas = useRef(null);   // 前の配信の再生の様子（同じ録画で実写 ⇔ 演出を切り替えたら引き継ぐ）
  useEffect(() => {
    if (!liveVideo) return undefined;
    const prev = liveWas.current?.rec && liveWas.current.rec === recKey ? liveWas.current.snap : null;
    liveVideo.resume(prev);
    let alive = true;
    fetchLiveInfo().then((info) => { if (alive && info?.overlay_zones) setLiveZones(info.overlay_zones); });
    return () => { alive = false; liveWas.current = { rec: recKey, snap: liveVideo.snapshot() }; liveVideo.destroy(); };
  }, [liveVideo]);   // eslint-disable-line react-hooks/exhaustive-deps
  usePageTitle(seatMode ? `入力（P${mySeat + 1}）` : '入力');
  useEffect(() => lockPageScroll(), []);

  const [game, setGame] = useState(null);
  const [config, setConfig] = useState(null);
  const [deckIndex, setDeckIndex] = useState({});
  const [decks, setDecks] = useState([[], []]);    // プレイヤーごとのデッキの一覧（ドローの選び直しの候補）
  const [error, setError] = useState(null);
  const [replayId, setReplayId] = useState(null);
  const replayIdRef = useRef(null); replayIdRef.current = replayId;
  const [state, setState] = useState(null);        // OcgGetState / 書き込み系の body
  // 席モード: 選んでいる途中（2 席で共有。OcgSeatProgress / OcgSeatState の progress。Issue #717）
  const [progress, setProgress] = useState(null);
  const [replay, setReplay] = useState(null);      // OcgGetReplay（replay / ops / injections）
  const [noReplay, setNoReplay] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [notice, setNotice] = useState(null);
  const [forceRetry, setForceRetry] = useState(null);
  const [truncateRetry, setTruncateRetry] = useState(null);   // デッキの取り込みで記録が止まる: { drop, run }
  const [tab, setTab] = useState('respond');
  const [showBoard, setShowBoard] = useState(true);
  const [videoEl, setVideoEl] = useState(null);
  const [videoSize, setVideoSize] = useState(null);
  const [fxSize, setFxSize] = useState(null);       // 演出（ゲーム映像）の大きさ。盤面・ゾーンの基準（videoSize＝実写）とは分ける
  const [time, setTime] = useState({ t: 0, d: 0, paused: true });
  const [focusKey, setFocusKey] = useState(null);
  const [pickingPlaces, setPickingPlaces] = useState([]);
  const [registerPick, setRegisterPick] = useState(null);
  const [tickerQueue, setTickerQueue] = useState([]);
  const [recoveryCheck, setRecoveryCheck] = useState(null);   // 止まったときの付け直しの確かめ（OcgRematchOps の apply なし）
  // ルーム（ゲームサーバー。Issue #765）: 席モード・見る専用だけ。本番の OcgSeatState に room が付いたらつなぎ替える
  const roomRef = useRef(null);
  const [roomState, setRoomState] = useState({ active: false, phase: 'poll', label: null });
  const [roomInfo, setRoomInfo] = useState(null);   // ルームの印 { wait, catchup, closeRequested, decided }
  const roomOn = roomState.active;
  const layout = useInputLayout();
  const compact = layout === 'compact';
  const panels = visiblePanels(layout, tab);

  const players = useMemo(() => playersFromGame(game), [game]);
  const input = state?.input ?? null;   // サーバーが作った入力の見え方（Issue #663 / #665）
  const query = input?.query ?? null;
  const names = useMemo(() => ({ ...Object.fromEntries(Object.entries(deckIndex).map(([code, c]) => [code, c.name])), ...(input?.names ?? {}) }), [deckIndex, input]);
  const thumbs = useMemo(() => Object.fromEntries(Object.entries(deckIndex).map(([code, c]) => [code, c.image])), [deckIndex]);
  const playerLabel = useCallback((p) => `${players[p]?.name ?? `P${p + 1}`}（P${p + 1}）`, [players]);
  const playerColors = useMemo(() => players.map((pl) => pl.color), [players]);   // 盤・行・枠の色。部品は usePlayerColors() で受ける
  const head = input?.head ?? null;
  // 開始前に画面で選んだ先攻（開始の問い合わせに答えるときに送る。Issue #670）
  const [firstPick, setFirstPick] = useState(null);
  const firstPlayer = firstPick ?? head?.firstPlayer ?? 0;
  const ops = useMemo(() => replay?.ops ?? [], [replay]);

  // --- 読み込み ---
  const revRef = useRef(null);   // 席モード: 見ている版（ocg_replays.rev。答えるときに expected_rev で送る）
  const loadReplay = async (id, alive = () => true) => {
    setReplayId(id);
    let loaded;
    try {
      if (seatMode) {
        const v = await ocg.loadSeat(id, mySeat);
        if (!alive()) return;
        // ルームが開いていればつなぎ替える（本番の state は古いので入れない）。無ければ今までどおり（onApply → applySeatView）
        if (roomRef.current) roomRef.current.seat(v); else applySeatView(v);
        return;
      }
      loaded = await ocg.loadReplay(id, HOLD);
    } catch (e) { if (alive()) setNotice(String(e?.message ?? e)); return; }
    if (!alive()) return;
    setState(loaded.state);
    setReplay(loaded.replay);
  };
  /** 席モードで答えるときに付けるもの（席と見ていた版） */
  const seatOpts = () => (seatMode ? { seat: mySeat, expectedRev: revRef.current } : {});
  /** 書き込みの行き先: ルームにつないでいる（再接続中も）ならルーム、無ければ null（本番の API） */
  const roomTo = () => (roomRef.current?.status.active ? roomRef.current : null);
  /** 本番の席向けの返り（seatView の形）を画面へ。ticker は前の状態があるときだけ（開いた直後に全部流さない） */
  const applySeatView = (v) => {
    if (v?.progress) setProgress(v.progress);
    if (!v?.changed) return;
    revRef.current = v.rev;
    const next = stateForSeat(v.state, mySeat);
    if (stateRef.current) {
      const items = addedTicker(stateRef.current?.input?.ticker, next?.input?.ticker);
      if (items.length) setTickerQueue((q) => [...q, ...items].slice(-TICKER_MAX));
    }
    stateRef.current = next;
    setState(next);
    setReplay(v.replay);
  };
  /** ルームの state を画面へ（形は本番の OcgSeatState と同じ。見せ終わり待ちのあいだは前の input を残して問い合わせだけ消す＝roomBody） */
  const applyRoomState = (msg) => {
    if (msg?.replay_id != null) { setReplayId((id) => id ?? msg.replay_id); setNoReplay(false); }
    setRoomInfo({ wait: msg?.wait ?? null, catchup: !!msg?.catchup, closeRequested: !!msg?.close_requested, decided: !!msg?.decided });
    applySeatView(ocg.seatView(roomBody(msg, stateRef.current?.input ?? null)));
  };
  const roomCallbacks = useRef({});
  roomCallbacks.current = { applySeatView, applyRoomState, reload: () => { const id = replayIdRef.current; if (id) loadReplay(id); } };
  // ルームの殻（席モード・見る専用）。つなぐかどうかは本番の返りの room で決まる（無ければ何もしない）
  useEffect(() => {
    if (!seatMode && !watchMode) return undefined;
    const link = new ocg.RoomLink({
      onApply: (v) => roomCallbacks.current.applySeatView(v),
      onState: (msg) => roomCallbacks.current.applyRoomState(msg),
      onProgress: (p) => { if (p) setProgress(p); },
      onReload: () => roomCallbacks.current.reload(),
      onNotice: (text) => setNotice(text),
      onChange: (st) => { setRoomState(st); if (!st.active) setRoomInfo(null); },
    });
    roomRef.current = link;
    return () => { link.destroy(); roomRef.current = null; };
  }, [seatMode, watchMode]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const info = await api.project('GetDuelGameInfo', { game_uuid: gameUuid });
        const g = info?.status === 'success' ? info.duel : null;
        if (!alive) return;
        if (!g) { setError('試合が見つかりません'); return; }
        setGame(g);
        Promise.resolve(api.project('GetFieldGenConfig', { game_id: g.id })).then((cfg) => { if (alive) setConfig(cfg?.config ?? null); }).catch(() => {});
        Promise.all(deckIdsOfGame(g).map((id) => (id ? Promise.resolve(api.project('GetDeckCards', { deck_id: id })).catch(() => null) : null))).then((results) => {
          if (!alive) return;
          const index = {};
          results.forEach((res) => (res?.cards ?? []).forEach((c) => { index[String(c.card_id)] = { name: c.card_name, image: c.thumbnail_url ?? c.download_url ?? null }; }));
          setDeckIndex(index);
          setDecks(results.map((res) => deckList(res?.cards ?? [])));
        });
        const rows = await ocg.listReplays(gameUuid).catch(() => []);
        const id = ocg.latestReplayId(rows);
        if (!alive) return;
        if (!id) { setNoReplay(true); return; }
        await loadReplay(id, () => alive);
      } catch (e) { if (alive) setError(String(e?.message ?? e)); }
    })();
    return () => { alive = false; };
  }, [gameUuid]);   // eslint-disable-line react-hooks/exhaustive-deps

  // --- 書き込み ---
  const stateRef = useRef(null); stateRef.current = state;
  /** call(force) を送り、返った状態を入れる。force で書けば通る 400 はフラグ（force_required）でやり直しボタンを出す。増えたイベントは ticker へ */
  const mutate = useCallback(async (call, { onOk = null } = {}) => {
    if (!replayId || busyRef.current) return;
    busyRef.current = true; setBusy(true); setNotice(null); setForceRetry(null);
    let res = null; let rep = null; let err = null;
    try {
      res = await call(false);
      if (!seatMode) rep = await ocg.getReplay(replayId);   // 席モードは返りに replay が入っている
    } catch (e) { err = String(e?.message ?? e); }
    // 通信を全部終えてから状態をまとめて入れる（先に入れると、その描画で走る自動で通す応答が busy に弾かれて止まる）
    busyRef.current = false; setBusy(false);
    if (err) setNotice(err);
    if (res?.ok && res.room) {
      // ルーム: 書いたあとの state は全員へ届いていて applyRoomState が入れ終えている（res.body はその state）
      setPickingPlaces([]);
      setRegisterPick(null);
      onOk?.(stateRef.current);
    } else if (res?.ok) {
      let body = res.body;
      if (seatMode && body?.state) {   // 席向けの返り（OcgSeatState と同じ形）→ 画面の形に
        const v = ocg.seatView(body);
        revRef.current = v.rev;
        if (v.progress) setProgress(v.progress);
        body = stateForSeat(v.state, mySeat);
        rep = v.replay;
      }
      const items = addedTicker(stateRef.current?.input?.ticker, body?.input?.ticker);
      if (items.length) setTickerQueue((q) => [...q, ...items].slice(-TICKER_MAX));
      setState(body);
      setPickingPlaces([]);
      setRegisterPick(null);
      onOk?.(body);
    } else if (res?.body?.stale) {
      setNotice('相手の席が先に書きました。取り直しました');   // 席モード: 版が古い（Issue #711）
      if (!res.room) await loadReplay(replayId);   // ルームは新しい state をもう送ってきている
    } else if (res) {
      setNotice(res?.body?.error ?? `HTTP ${res?.status}`);
      if (res?.body?.force_required) setForceRetry(() => () => mutate(() => call(true), { onOk }));
    }
    if (rep) setReplay(rep);
  }, [replayId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // preview（録画が付く前のライブ）の時刻は録画の時刻ではないので付けない（無ければサーバーが「今 − 録画を始めた時間」で埋める。Issue #744）
  const videoMsNow = useCallback(() => (!previewMode && videoEl && Number.isFinite(videoEl.currentTime) ? Math.round(videoEl.currentTime * 1000) : null), [videoEl, previewMode]);
  /** 今の問い合わせに答える（押したもの → 選択肢。inputView.queryAnswer）。core への応答に直すのはサーバー（OcgAnswer） */
  const answerWith = useCallback((intent) => {
    const ans = queryAnswer(query, intent);
    if (!ans) { setNotice('その答えは今の問い合わせの選択肢にありません'); return undefined; }
    const videoMs = videoMsNow();
    return mutate((force) => (roomTo() ? roomTo().answer({ answer: ans, rev: revRef.current, videoMs, force })
      : ocg.answer(replayId, ans, { videoMs, ...ANSWER, force, ...seatOpts() })));
  }, [mutate, replayId, videoMsNow, query]);   // eslint-disable-line react-hooks/exhaustive-deps
  const respond = useCallback((payload) => answerWith({ payload }), [answerWith]);
  /** 開始のじゃんけんで手を出す（席ごとの問い合わせの id に。ルームだけ。Issue #809） */
  const pickRps = (q, index) => mutate((force) => roomTo().answer({ answer: { id: q.id, picks: [index] }, rev: revRef.current, force }));
  // 戻すのは最後の人の答えまで（後ろの自動で通した分も一緒に。Issue #658）。消える人の答えの時刻へ動画を戻す（その問い合わせに答え直すため。ユーザー要望）
  const undo = useCallback(() => {
    const allOps = replay?.ops ?? [];
    const count = Math.max(1, input?.undo_count ?? 1);
    const ms = allOps[allOps.length - count]?.video_ms ?? input?.last_ms ?? null;
    const call = roomTo() ? () => roomTo().undo({ rev: revRef.current })
      : seatMode ? () => ocg.seatUndo(replayId, mySeat, revRef.current) : () => ocg.undo(replayId, count, HOLD);
    return mutate(call, { onOk: () => { if (videoEl && ms != null && !previewMode) { videoEl.pause?.(); videoEl.currentTime = Math.max(0, ms / 1000); } } });
  }, [mutate, replayId, replay, videoEl, input]);   // eslint-disable-line react-hooks/exhaustive-deps
  /**
   * 「さっき通したところ」へ戻って発動する（Issue #658）: そのチェーン確認の応答から後ろを消し（rewindTo）、戻った問い合わせがそのチェーン確認か確かめてから
   * { chain: index } で答える。時刻は通したときの応答の時刻（無ければ今の動画の時刻）。戻せたが発動できないときは、戻った問い合わせを出したまま止める
   */
  const rewindAndActivate = useCallback((chance, option) => {
    const allOps = replay?.ops ?? [];
    const ms = allOps.find((o) => o.seq === chance.seq)?.video_ms ?? videoMsNow();
    let problem = null;
    const room = roomTo();
    return mutate(async () => {
      // ルームは本番の OcgUndo と同じ「末尾から count 手」（undo の count）
      const back = room ? await room.undo({ rev: revRef.current, count: Math.max(0, allOps.length - chance.seq) })
        : await ocg.rewindTo(replayId, chance.seq, allOps.length, HOLD);
      if (!back?.ok) return back;
      const p = back.body?.state?.pending ?? back.body?.pending;
      if (p?.msg !== 'MSG_SELECT_CHAIN' || (chance.msgIndex != null && p.msg_index !== chance.msgIndex)) {
        problem = '戻した先がそのチェーン確認になりませんでした（今の問い合わせに答え直してください）';
        return back;
      }
      const q = back.body?.input?.query;
      const ans = queryAnswer(q, { payload: { chain: option.index } });
      if (!ans) { problem = '戻した先の問い合わせにそのカードの発動がありませんでした'; return back; }
      const res = room ? await room.answer({ answer: ans, videoMs: ms })   // 戻した直後なので版は見ない
        : await ocg.answer(replayId, ans, { videoMs: ms, ...ANSWER, ...(seatMode ? { seat: mySeat } : {}) });
      if (!res?.ok) { problem = `戻しましたが発動できませんでした: ${res?.body?.error ?? `HTTP ${res?.status}`}`; return back; }
      return res;
    }, { onOk: () => { if (problem) setNotice(problem); } });
  }, [mutate, replayId, replay, videoMsNow]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setStart = () => {
    const ms = videoMsNow();
    if (ms == null) return;
    // 初めて開始位置を決めたら、開始時のイベント（最初の応答より前＝op_seq の無いもの。初手のドローなど）を情報帯に流す
    const firstTime = head?.startVideoMs == null;
    const onOk = firstTime ? (body) => {
      const items = startTicker(body?.input, body?.events);
      if (items.length) setTickerQueue((q) => [...q, ...items].slice(-TICKER_MAX));
    } : null;
    // 開始はサーバーの開始の問い合わせ（source start）への答え: 先攻（画面で選んでおいたもの）と開始位置（今の動画の時刻）。Issue #670
    // 開始の問い合わせが出ない古いデータ（開始位置が無いのに応答がある）だけ従来どおり OcgUpdateReplay
    if (query?.source === 'start') {
      const ans = { id: query.id, picks: [firstPlayer] };
      mutate((force) => (roomTo() ? roomTo().answer({ answer: ans, rev: revRef.current, videoMs: ms, force })
        : ocg.answer(replayId, ans, { videoMs: ms, force, ...seatOpts() })), { onOk });
      return;
    }
    if (roomTo()) { setNotice('ルームでは開始の問い合わせからだけ開始できます'); return; }
    mutate((force) => ocg.updateReplay(replayId, { start_video_ms: ms }, { force, ...HOLD }), { onOk });
  };
  /** デッキレシピの今の中身を replay の写しへ取り込む（Issue #618）。変わっていたら取り直して、何が変わったかを出す */
  const syncDeck = async ({ truncate = false, quiet = false } = {}) => {
    if (!replayId || !game || busyRef.current) return;
    busyRef.current = true; setBusy(true); setForceRetry(null); setTruncateRetry(null);
    let res = null;
    try { res = await ocg.syncDeck(replayId, deckIdsOfGame(game), { rematch: true, truncate }); } catch (e) { res = { ok: false, body: { error: String(e?.message ?? e) } }; }
    busyRef.current = false; setBusy(false);
    if (res?.ok && res.body?.changed) {
      const name = (code) => names[String(code)] ?? code;
      const teams = Object.entries(res.body.teams ?? {}).map(([t, d]) => {
        const plus = [...(d.added ?? []), ...(d.added_extra ?? [])].map((c) => `＋${name(c)}`);
        const minus = [...(d.removed ?? []), ...(d.removed_extra ?? [])].map((c) => `−${name(c)}`);
        return plus.length || minus.length ? `P${Number(t) + 1}: ${[...plus, ...minus].join(' ')}` : null;
      }).filter(Boolean);
      await loadReplay(replayId);
      setNotice(`デッキレシピの変更を取り込みました（${teams.join(' / ') || '並びだけ'}${res.body.rematched ? `・付け直した応答 ${res.body.rematched} 手` : ''}）`);
    } else if (res?.ok) {
      if (!quiet) setNotice('デッキレシピと replay のデッキは同じです');
    } else {
      setNotice(`デッキレシピを取り込めません: ${res?.body?.error ?? `HTTP ${res?.status}`}`);
      // 止まる手から先を消せば取り込める（強制して反映は無い。Issue #954）
      if (res?.body?.truncate_required) setTruncateRetry({ drop: res.body.drop_ops ?? 0, run: () => syncDeck({ truncate: true }) });
    }
  };
  // 開いたときに 1 回（見る専用では書き込まないので呼ばない）
  const syncedFor = useRef(null);
  useEffect(() => {
    if (watchMode || seatMode || !replayId || !game || syncedFor.current === replayId) return;   // 席モードは 2 台が同時に書かないよう取り込まない
    syncedFor.current = replayId;
    syncDeck({ quiet: true });
  }, [replayId, game, watchMode, seatMode]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 止まったら、選んだカードで付け直せるかを確かめておく（書かない）
  const stopped = state?.stopped ?? null;
  const stoppedKey = stopped ? `${replayId}:${stopped.at_seq}:${stopped.reason}:${replay?.ops?.length ?? 0}` : null;
  useEffect(() => {
    setRecoveryCheck(null);
    if (!stoppedKey || watchMode || roomOn) return undefined;   // ルームのあいだ本番の記録は古い
    let alive = true;
    ocg.rematchOps(replayId).then((res) => {
      if (!alive) return;
      setRecoveryCheck(res?.ok ? res.body : { error: res?.body?.error ?? `HTTP ${res?.status}` });
    }).catch((e) => { if (alive) setRecoveryCheck({ error: String(e?.message ?? e) }); });
    return () => { alive = false; };
  }, [stoppedKey, watchMode, roomOn]);   // eslint-disable-line react-hooks/exhaustive-deps
  /** 飛ばす（見せ終わり待ちを解く）・追いつくまで待たない・対戦を終える（ルームだけ。Issue #765） */
  const roomCommand = async (fn, failText) => {
    const room = roomTo();
    if (!room) return;
    const res = await fn(room);
    if (!res?.ok) setNotice(`${failText}: ${res?.body?.error ?? '?'}`);
    return res;
  };
  const skipWait = () => roomCommand((r) => r.skip(), '飛ばせませんでした');
  const setCatchup = (on) => roomCommand((r) => r.catchup(on), '切り替えられませんでした');
  const closeMatch = async () => {
    // 押し間違えないよう確かめる（対戦中いつでも押せる。決着では自動で閉じない）
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('対戦を終えますか？（ルームを閉じて記録を本番へ返し、録画を止めます）')) return;
    const res = await roomCommand((r) => r.close(), '対戦を終えられませんでした');
    if (res?.ok) setNotice('対戦を終えます。記録を本番へ返しています…');
  };
  const rematchStopped = (dropAfterStop) => mutate(() => ocg.rematchOps(replayId, { apply: true, dropAfterStop }));
  const truncateFromStop = (count) => mutate(() => ocg.undo(replayId, count, HOLD));

  /** 入力の決まりを変える（両席に効く。Issue #668）。変えたら状態を取り直す（問い合わせ・自動で通すかが変わる） */
  const setPolicy = async (patch) => {
    if (!replayId || busyRef.current) return;
    if (roomTo()) {   // ルームが持つ入力の決まり（両席に効く。変わった state が全員へ届く）
      const res = await roomTo().policy(patch);
      if (!res?.ok) setNotice(res?.body?.error ?? '入力の決まりを変えられませんでした');
      return;
    }
    try { await api.ocgSetInputPolicy({ replay_id: replayId, policy: patch }); } catch (e) { setNotice(String(e?.message ?? e)); return; }
    await loadReplay(replayId);
  };
  /** ランダムで遊ぶか（Issue #809）。開始前（応答も登録も無いとき）だけサーバーが受ける */
  const setRandomPlay = (on) => mutate((force) => ocg.updateReplay(replayId, { random_play: on }, { force, ...HOLD }));
  /** 登録の項目（ドロー・コイン・サイコロ・ランダム選択）を選び直す: OcgAnswer の register_key。登録の中身への直し方はサーバー（Issue #669） */
  const registerAnswer = (item, ans) => mutate((force) => (roomTo() ? roomTo().answer({ answer: { register_key: item.key, ...ans }, rev: revRef.current, force })
    : ocg.answer(replayId, { register_key: item.key, ...ans }, { force, ...seatOpts() })));
  /** 先攻を選ぶ。開始の問い合わせがあれば画面で覚えておくだけ（開始で一緒に送る）。無い古いデータは従来どおり書く */
  const setFirst = (player) => {
    if (query?.source === 'start') { setFirstPick(player); return undefined; }
    return mutate((force) => ocg.updateReplay(replayId, { first_player: player }, { force, ...HOLD }));
  };
  /** replay を作る（1 試合 1 replay。先攻は P1 のまま、両デッキを取り込む）。ログを1からやり直すのはデュエル一覧（ResetReplayButton） */
  const startReplay = async () => {
    if (!game || busyRef.current) return;
    busyRef.current = true; setBusy(true); setNotice(null); setForceRetry(null);
    try {
      const id = await ocg.createReplayForGame(game, gameUuid);
      setNoReplay(false);
      await loadReplay(id);
    } catch (e) { setNotice(String(e?.message ?? e)); } finally { busyRef.current = false; setBusy(false); }
  };

  // デュエル開始前（開始位置が未設定・動画がそれより前）は core のことを画面に出さない
  // 動画があるかは要素ではなく試合の URL で見る（要素ができる前の描画で「動画なし＝開始済み」と判定して自動で答えてしまうため）
  // ライブは試合のフィールド動画を出さない。preview は映像として数えない（開始位置を録画の時刻で決められない。開始の問い合わせは時刻なしで答える）
  // 開始のじゃんけん（ランダムで遊ぶ試合のルーム。Issue #809）: 席モードは自分の席の分、そうでなければまだ出していない席の分
  const rpsOn = !!input?.rps && input.rps.winner == null;
  const rpsQuery = rpsOn ? (seatMode ? input.seat_queries?.[String(mySeat)] ?? null : query) : null;
  const beforeStart = !!head && isBeforeStart({ hasVideo: liveMode ? !previewMode : !!(game?.movie1_download_url ?? game?.movie2_download_url), startMs: head.startVideoMs, videoMs: Math.round((time.t || 0) * 1000) });

  // 席モード: 相手の席の操作を取り直す（版が同じなら 1 行読むだけ。増えたイベントは情報帯へ）。replay がまだ無ければ出来るのを待つ。
  // ルームにつないでいるあいだは読まない（ルームが送ってくる）。再接続中は本番に入口を聞き直す（間を空けて。roomLink の pollDelay）
  useEffect(() => {
    if (!seatMode) return undefined;
    let alive = true;
    let running = false;
    let retryAt = 0;
    const tick = async () => {
      const link = roomRef.current;
      if (link && !link.needsPoll()) return;
      const retry = link?.status.phase === 'retry';
      if (running || (!retry && busyRef.current) || (typeof document !== 'undefined' && document.hidden)) return;
      if (retry) {
        if (Date.now() < retryAt) return;
        retryAt = Date.now() + link.pollDelay(SEAT_POLL_MS);
      }
      running = true;
      try {
        if (!replayId) {
          const id = ocg.latestReplayId(await ocg.listReplays(gameUuid).catch(() => []));
          if (alive && id) { setNoReplay(false); await loadReplay(id, () => alive); }
          return;
        }
        const v = await ocg.loadSeat(replayId, mySeat, retry ? null : revRef.current);
        if (!alive) return;
        if (link && (retry || roomOf(v))) { link.seat(v); return; }
        if (v.progress) setProgress(v.progress);   // 版は同じでも途中経過は変わる（相手の席が指した）
        if (!v.changed || busyRef.current) return;
        applySeatView(v);
      } catch (e) { /* 次の回に取り直す */ } finally { running = false; }
    };
    const t = setInterval(tick, SEAT_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [seatMode, mySeat, replayId, gameUuid]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 見る専用: 入力している端末の操作を取り直す（増えたイベントは情報帯へ）。replay がまだ無ければ出来るのを待つ
  const watchRev = useRef(null);
  useEffect(() => {
    if (!watchMode) return undefined;
    let alive = true;
    let running = false;
    const tick = async () => {
      if (running || (typeof document !== 'undefined' && document.hidden)) return;
      running = true;
      try {
        if (!replayId) {
          const id = ocg.latestReplayId(await ocg.listReplays(gameUuid).catch(() => []));
          if (alive && id) { setNoReplay(false); await loadReplay(id, () => alive); }
          return;
        }
        // ルームが開いているか（本番が席なしの OcgSeatState に room を付ける）。開いていればルームの観戦へつなぎ替える
        const link = roomRef.current;
        if (link && !link.needsPoll()) return;
        const retry = link?.status.phase === 'retry';
        const probe = link ? await ocg.loadSeat(replayId, null, retry ? null : watchRev.current).catch(() => null) : null;
        if (!alive) return;
        if (probe) watchRev.current = probe.rev;
        if (link && probe && (retry || roomOf(probe))) { link.seat(probe); return; }
        if (retry) return;
        const loaded = await ocg.loadReplay(replayId, HOLD);
        if (!alive) return;
        const items = addedTicker(stateRef.current?.input?.ticker, loaded.state?.input?.ticker);
        if (items.length) setTickerQueue((q) => [...q, ...items].slice(-TICKER_MAX));
        setState(loaded.state);
        setReplay(loaded.replay);
      } catch (e) { /* 次の回に取り直す */ } finally { running = false; }
    };
    const t = setInterval(tick, WATCH_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [watchMode, replayId, gameUuid]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ticker: 先頭を TICKER_MS 出したら次へ（先頭が変わったときだけ時計を回し直す。後ろに積まれても今のを延ばさない）
  const tickerHead = tickerQueue[0] ?? null;
  const [lastTickerText, setLastTickerText] = useState(null);   // 流し終えたあとも最後の文を薄く残す
  useEffect(() => { if (tickerHead) setLastTickerText(tickerHead.text); }, [tickerHead]);
  useEffect(() => {
    if (!tickerHead) return undefined;
    const t = setTimeout(() => setTickerQueue((q) => q.slice(1)), TICKER_MS);
    return () => clearTimeout(t);
  }, [tickerHead]);

  // --- タブ・動画 ---
  const switchTab = (next) => {
    setTab(next);
    if (!seatMode && pausesVideo(next) && videoEl && !videoEl.paused) videoEl.pause?.();   // 席モードは映像を止めない
  };
  // スマホは再生タブへ移る。PC は再生バーが常に出ているのでタブはそのまま
  const playAndWatch = () => { videoEl?.play?.()?.catch?.(() => {}); if (compact) setTab('play'); };
  /** PC: 右のパネル（応答・登録）を押したら動画を止める（スマホの「入力するタブに切り替えたら止める」と同じ。応答の時刻＝止めた位置） */
  const pauseForInput = () => { if (!seatMode && videoEl && !videoEl.paused) videoEl.pause?.(); };
  const togglePlay = () => { if (!videoEl) return; if (videoEl.paused) videoEl.play?.()?.catch?.(() => {}); else videoEl.pause?.(); };
  const seekTo = (ms) => { if (videoEl && ms != null && !previewMode) videoEl.currentTime = Math.max(0, ms / 1000); };   // preview は巻き戻せない
  /** 今の位置から sec 秒。**state の時刻（seeked のあとに入る）ではなく動画の要素から読む** —— 続けて押すと、前のシークが終わる前の古い位置から数えてしまう */
  const skipBy = (sec) => seekTo((videoMsNow() ?? Math.round((time.t || 0) * 1000)) + sec * 1000);
  const speechReview = useSpeechSegments(gameUuid);
  const speech = speechReview.segments;
  const videoMs = Math.round((time.t || 0) * 1000);
  const speechIdx = speechIndexAt(speech, videoMs);
  // 下半分の情報帯の発話: 今の時刻に喋っている採用の発話（笑いは出さない。旧演出プレビューの字幕と同じ）
  const subtitles = useMemo(() => subtitlesAt(speech.filter((sg) => sg.speaker !== 'laughter'), videoMs), [speech, videoMs]);
  const lastMs = input?.last_ms ?? head?.startVideoMs ?? null;
  const needsStart = !!head && head.startVideoMs == null && !!videoEl && !previewMode;
  const movieUrl = liveMode ? null : (game?.movie1_download_url ?? game?.movie2_download_url ?? null);
  const zones = useMemo(() => resolveZones(liveZones ? { overlay_zones: liveZones } : config, videoSize), [config, liveZones, videoSize]);
  // ライブ: 端から何秒遅れているか（duration＝届いた中でいちばん新しい時刻）と、端へ戻る
  const live = liveVideo ? { lag: Math.max(0, (time.d || 0) - (time.t || 0)), paused: time.paused, onLive: () => liveVideo.goLive() } : null;
  const views = useMemo(() => availableViews(videoSize, { fx: fxAvailable }), [videoSize, fxAvailable]);
  const videoProps = {
    onLoadedMetadata: (e) => {
      const v = e.currentTarget;
      (fxView ? setFxSize : setVideoSize)({ w: v.videoWidth, h: v.videoHeight });   // 演出の大きさで盤面・ゾーンの基準を変えない
      setTime({ t: v.currentTime, d: v.duration, paused: v.paused });
    },
    onTimeUpdate: (e) => { const v = e.currentTarget; setTime({ t: v.currentTime, d: v.duration, paused: v.paused }); },
    onPlay: (e) => { const { paused } = e.currentTarget; setTime((s) => ({ ...s, paused })); },
    onPause: (e) => { const { paused } = e.currentTarget; setTime((s) => ({ ...s, paused })); },
    onSeeked: (e) => { const v = e.currentTarget; setTime({ t: v.currentTime, d: v.duration, paused: v.paused }); },
  };

  // --- 登録 ---
  const grayKeys = useMemo(() => new Set(input?.gray_keys ?? []), [input]);
  // 登録の項目（一次データの形のまま。value_text・board_key はサーバーが付ける）
  const registerItems = useMemo(() => input?.register?.raw ?? [], [input]);
  const unconfirmedCount = registerItems.filter((i) => !i.confirmed).length;
  // 登録待ち（Issue #612）: サービスが未確認のカードのイベントで止めた項目
  // 席モードの登録の問い合わせはこの席の項目（初手は両席に同時に出る＝サービスが止めた項目と違うことがある。Issue #750）
  const holdKey = (seatMode && query?.source === 'register' ? query.register_key : null) ?? input?.register?.hold_key ?? null;
  const holdItem = useMemo(() => registerItems.find((i) => i.key === holdKey) ?? null, [registerItems, holdKey]);
  /** 登録待ちの項目を開く（ドローは登録タブの横長のデッキ一覧、コイン・サイコロ・ランダム選択はダイアログ）。スマホはドローのとき登録タブへ */
  const openRegister = (item) => { setRegisterPick(item); if (compact && item?.type === 'draw') switchTab('register'); };

  // --- 応答 ---
  // 応答タブの中身はサーバーの問い合わせの view。自動で通すチェーン確認は本番が書き込みのあとに通すので画面には来ない（来ても人が答えられるようそのまま出す。Issue #927）
  const rview = useMemo(() => query?.view ?? { type: 'none' }, [query]);
  const candidateKeys = useMemo(() => new Set((rview.groups ?? []).flatMap((g) => g.tiles.map((t) => t.boardKey)).filter(Boolean)), [rview]);
  const zoneMarks = useMemo(() => {
    if (rview.type !== 'place') return new Set();
    const keyOf = (p) => (query?.options ?? []).find((o) => o.place && o.place.player === p.player && o.place.location === p.location && o.place.sequence === p.sequence)?.key ?? null;
    const picked = new Set(pickingPlaces.map(keyOf));
    return new Set((query?.options ?? []).filter((o) => o.place && (!picked.size || picked.has(o.key))).map((o) => zoneKeyOfPlaceKey(o.key)).filter(Boolean));
  }, [rview, pickingPlaces, query]);
  const onFocus = useCallback((key) => setFocusKey(key), []);
  // 今の区間で自動で通したチェーン確認のうち、発動できたもの（両プレイヤー。この画面は 1 人で両方を入力する。Issue #658）
  // 席モードは自分の席の分だけ（相手の番で止まっていても戻って発動できる。Issue #717）
  const chances = useMemo(() => (watchMode ? [] : seatChances(input?.skipped_chances, seatMode ? mySeat : null)), [input, watchMode, seatMode, mySeat]);
  // 席モード: 選んでいる途中は 2 席で共有（応答タブの選択・相手の選択に自分のカードを指す。Issue #717）
  const seatPicks = useMemo(() => progressPicks(progress, query), [progress, query]);
  const setSeatPicks = useCallback(async (next) => {
    if (!seatMode || !query?.id || !replayId) return;
    const prev = seatPicks;
    setProgress((p) => ({ query_id: query.id, picks: next.map((i) => ({ index: i, seat: mySeat })), seq: p?.seq ?? 0 }));   // 先に画面へ
    let last = null;
    const room = roomTo();   // ルームは progress を全員へ送り返す（onProgress）
    const send = (op, i) => (room ? room.progress({ queryId: query.id, op, index: i }) : ocg.seatProgress(replayId, mySeat, query.id, op, { index: i }));
    for (const i of prev.filter((x) => !next.includes(x))) last = await send('remove', i);
    for (const i of next.filter((x) => !prev.includes(x))) last = await send('add', i);
    if (last?.ok && last.body?.progress) setProgress(last.body.progress);
  }, [seatMode, query, replayId, seatPicks, mySeat]);
  const helper = useMemo(() => helperOptions(query, seatMode ? mySeat : null), [query, seatMode, mySeat]);
  const behindLast = panels.respond && lastMs != null && videoEl && videoMs < lastMs;

  // --- 登録（続き） ---
  /** 「このままで合っている」（今の値で確定）。登録の中身はサーバーが決める（OcgAnswer の register_key） */
  const confirmItem = (item) => mutate((force) => (roomTo() ? roomTo().answer({ answer: { register_key: item.key, confirm: true }, rev: revRef.current, force })
    : ocg.answer(replayId, { register_key: item.key, confirm: true }, { force, ...seatOpts() })));
  // 選び直し・OK で確定したら、同じプレイヤーの次の未確認のドローを開く（確定できたときだけ。失敗したら進まない）
  const advanceFrom = useRef(null);
  useEffect(() => {
    const from = advanceFrom.current;
    if (!from || busy) return;
    advanceFrom.current = null;
    if (!registerItems.find((i) => i.key === from.key)?.confirmed) return;
    // 次の登録待ち（サービスが止めた項目）があればそれを、無ければ同じプレイヤーの次の未確認のドロー
    const next = registerItems.find((i) => i.key === holdKey) ?? nextUnconfirmedDraw(registerItems, from);
    if (next) setRegisterPick(next);
  }, [registerItems, busy, holdKey]);
  const registerFocus = useMemo(() => {
    if (!registerPick || registerPick.type !== 'draw') return null;
    return registerItems.find((i) => i.key === registerPick.key)?.board_key ?? null;
  }, [registerPick, registerItems]);

  // 両方並べているとき（both）は、登録で選んでいるドローがあればそちらを指す
  const stageFocus = panels.register && (registerFocus || !panels.respond) ? registerFocus : panels.respond ? focusKey : null;
  const stageMarks = panels.respond && !beforeStart ? candidateKeys : new Set();

  // --- キーボード（PC の並べ方のときだけ。表は pcKeys.js） ---
  const canUndo = !!replayId && (state?.consumed ?? ops.length) > 0;
  const keyRun = useRef(null);
  keyRun.current = {
    // 席モードは再生の操作を持たない（再生バーも出さない。Issue #717）
    toggle: togglePlay,
    back5: () => skipBy(-5), fwd5: () => skipBy(5), back1: () => skipBy(-1), fwd1: () => skipBy(1),
    prevSpeech: () => seekTo(prevSpeechMs(speech, videoMs)), nextSpeech: () => seekTo(nextSpeechMs(speech, videoMs)),
    last: () => seekTo(lastMs),
    // 今の発話の採否（シミュレータの F キーと同じ: 開始済みのうち一番新しい区間。Issue #998）
    toggleSpeech: () => {
      if (watchMode) return;
      const seg = toggleTargetAt(speech, videoMsNow() ?? videoMs);
      if (seg) speechReview.setEnabled(seg.id, seg.enabled === false);
    },
    undo: () => { if (!watchMode && canUndo && !busy && !beforeStart) undo(); },
  };
  if (seatMode) Object.keys(keyRun.current).forEach((k) => { if (k !== 'undo') keyRun.current[k] = () => {}; });
  useEffect(() => {
    if (compact) return undefined;
    const onKeyDown = (e) => {
      const action = actionOf(e);
      if (!action) return;
      // ボタンにフォーカスが残っていると Space がそのボタンをもう一度押す（同じ応答を 2 回送る）。既定の動作を止めてフォーカスも外す
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement && document.activeElement.tagName === 'BUTTON') document.activeElement.blur();
      keyRun.current?.[action]?.();
    };
    const onKeyUp = (e) => { if (e.code === 'Space' && actionOf(e)) e.preventDefault(); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
  }, [compact]);

  const noticeBar = (
    <>
      {seatMode ? <MatchPanel seat={mySeat} compact onMatch={followMatch ? onMatch : null} /> : null}
      {roomOn ? (
        <RoomBar status={roomState} info={roomInfo} canWrite={seatMode} busy={busy} onCatchup={setCatchup} onClose={closeMatch} />
      ) : null}
      {error ? <div role="alert" style={{ background: '#412', color: '#fcc', padding: '6px 10px', fontSize: 13 }}>{error}</div> : null}
      {notice ? (
        <div role="status" data-testid="input-notice" style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#2a2340', color: '#fde', padding: '6px 10px', fontSize: 12 }}>
          <span style={{ flex: 1, minWidth: 0 }}>{notice}</span>
          {forceRetry ? <button type="button" data-testid="force-retry" disabled={busy} style={btn('danger')} onClick={forceRetry}>記録済みの操作が合わなくなっても変える</button> : null}
          {truncateRetry ? <button type="button" data-testid="truncate-retry" disabled={busy} style={btn('danger')}
            onClick={() => { if (window.confirm(`止まる手から先の記録（${truncateRetry.drop} 手）を消してデッキを取り込みますか？`)) truncateRetry.run(); }}>
            止まる手から先（{truncateRetry.drop} 手）を消して取り込む</button> : null}
          <button type="button" aria-label="閉じる" onClick={() => { setNotice(null); setForceRetry(null); }} style={{ background: 'none', border: 'none', color: '#fde', cursor: 'pointer' }}>✕</button>
        </div>
      ) : null}
      {liveVideo && !liveVideo.supported ? <div role="alert" data-testid="live-unsupported" style={{ background: '#412', color: '#fcc', padding: '6px 10px', fontSize: 12 }}>この端末のブラウザではライブ映像を再生できません（VideoDecoder が無い）。iPad / iPhone の Safari か、新しい Chrome / Edge で開いてください</div> : null}
      {stopped ? (
        <RecoveryPanel stopped={stopped} total={ops.length} opMs={ops.find((o) => o.seq === stopped.at_seq)?.video_ms ?? null} check={recoveryCheck} busy={busy}
          onSeek={seekTo} onRematch={rematchStopped} onTruncate={truncateFromStop} />
      ) : null}
    </>
  );

  const playContent = !panels.play ? null : (
      <PlaybackTab time={time} hasVideo={!!videoEl} busy={busy} hasPrev={speechIdx >= 0} hasNext={nextSpeechMs(speech, videoMs) != null} lastMs={lastMs}
        needsStart={needsStart} speechText={speech[speechIdx]?.text ?? null} live={live}
        hasReplay={!!replayId && !!head} firstPlayer={firstPlayer} players={players}
        onPlayPause={togglePlay} onSeek={seekTo} onSkip={skipBy}
        onPrev={() => seekTo(prevSpeechMs(speech, videoMs))} onNext={() => seekTo(nextSpeechMs(speech, videoMs))} onSetStart={setStart}
        onSetFirst={setFirst} />
  );
  const respondContent = !panels.respond ? null : (
    noReplay ? (
      <div data-testid="input-no-replay" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <strong>この試合の再現エンジンの replay がまだありません</strong>
        <div style={{ fontSize: 13, opacity: 0.85 }}>両プレイヤーのデッキを取り込んで replay を作ります。先攻と開始位置はあとから決められます。</div>
        <button type="button" disabled={busy || !game} style={btn('primary')} onClick={startReplay}>リプレイを開始する</button>
      </div>
    ) : roomOn && roomInfo?.wait ? (
      // 見せ終わり待ち（ルーム）: 演出を見せ終わるまで次の問い合わせは来ない。飛ばせばすぐ次へ
      <div data-testid="room-wait" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <strong>演出を見せています…</strong>
        <div style={{ fontSize: 13, opacity: 0.85 }}>見せ終わると次の問い合わせが出ます</div>
        <button type="button" data-testid="room-skip" disabled={busy} style={btn('primary')} onClick={skipWait}>飛ばす</button>
        {canUndo ? <button type="button" disabled={busy} style={btn()} onClick={undo}>戻す（最後の選択を取り消す）</button> : null}
      </div>
    ) : seatMode && query && !isMyTurn(query, mySeat) ? (
      <div data-testid="seat-waiting" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong>{playerLabel(Number(query.player ?? 0))}の番です</strong>
        <div style={{ fontSize: 13, opacity: 0.85 }}>{query.message ?? '相手が答えるのを待っています'}</div>
        {canUndo ? <button type="button" disabled={busy} style={btn()} onClick={undo}>戻す（最後の選択を取り消す）</button> : null}
        {helper.size ? (
          // 相手がカードを選んでいる: 候補のうち自分がコントロールするカードはこの席からも指せる（決めるのは相手。Issue #717）
          <div data-testid="seat-helper" style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>相手が選んでいます。自分のカードはここから指せます（決めるのは相手）</div>
            {(query.options ?? []).filter((o) => helper.has(o.index)).map((o) => {
              const tile = (query.view?.groups ?? []).flatMap((g) => g.tiles ?? []).find((t) => t.index === o.index) ?? o;
              const on = seatPicks.includes(o.index);
              return (
                <div key={o.index} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#151c30', borderRadius: 8 }}>
                  {thumbs[String(o.code)] ? <img alt="" src={thumbs[String(o.code)]} style={{ width: 30, height: 44, objectFit: 'cover', borderRadius: 3 }} /> : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{names[String(o.code)] ?? o.name ?? o.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{ownLocationText(tile.location_text ?? o.location)}</div>
                  </div>
                  <button type="button" data-testid={`seat-helper-${o.index}`} disabled={busy} style={btn(on ? 'plain' : 'primary')}
                    onClick={() => setSeatPicks(on ? seatPicks.filter((i) => i !== o.index) : [...seatPicks, o.index])}>{on ? '外す' : '指す'}</button>
                </div>
              );
            })}
          </div>
        ) : null}
        {chances.length ? <SkippedChances chances={chances} names={names} thumbs={thumbs} players={players} busy={busy || !state} onRewind={rewindAndActivate} /> : null}
      </div>
    ) : rpsOn ? (
      <RpsPanel query={rpsQuery} rps={input.rps} players={players} busy={busy || !roomTo()} onPick={pickRps} />
    ) : beforeStart ? (
      <PreStartPanel message={input?.rps ? [rpsResultText(input.rps, players), query?.message].filter(Boolean).join('　') : null} startMs={head?.startVideoMs ?? null} videoMs={videoMs} busy={busy} hasVideo={!!videoEl}
        firstPlayer={firstPlayer} players={players} onSetFirst={setFirst} onSetStart={setStart} onSeekStart={() => seekTo(head?.startVideoMs)} onPlay={videoEl ? playAndWatch : null} />
    ) : (
      <RespondTab key={`${query?.id ?? 'none'}:${!!query?.auto}`} view={rview} query={query} board={state?.board ?? null} names={names} thumbs={thumbs} players={players} busy={busy || !state}
        videoEl={fxView ? null : videoEl} videoSize={videoSize} zones={zones} canUndo={canUndo} onRespond={respond} onAnswer={answerWith} onUndo={undo} onPlay={videoEl ? playAndWatch : null} onFocus={onFocus} onPlaces={setPickingPlaces}
        onRegister={holdItem ? () => openRegister(holdItem) : null}
        picked={seatMode ? seatPicks : null} onPicked={seatMode ? setSeatPicks : null}
        extra={chances.length ? <SkippedChances chances={chances} names={names} thumbs={thumbs} players={players} busy={busy || !state} onRewind={rewindAndActivate} /> : null}>
        {needsStart ? <div style={{ padding: '6px 10px', fontSize: 12, background: '#2a2340' }}>デュエルの開始位置が決まっていません{compact ? '（再生タブで決められます）' : ''}</div> : null}
        {query?.auto_blocked === 'unconfirmed' ? <div data-testid="respond-hold-auto" style={{ padding: '6px 10px', fontSize: 12, background: '#2a2340' }}>未確認のカードがあるので自動では通しません。登録タブで確定するか、そのまま答えてください</div> : null}
        {behindLast ? <div data-testid="respond-behind" style={{ padding: '6px 10px', fontSize: 12, background: '#3a2a14' }}>動画が最後の応答（{fmtTime(lastMs / 1000)}）より前です。この位置で答えると時刻が前後します</div> : null}
      </RespondTab>
    )
  );
  // 今の中身で（並び次第でめくる効果は、足すたびに確定待ちの並びが変わる。Issue #926）
  const pickingDraw = registerPick?.type === 'draw' ? (registerItems.find((i) => i.key === registerPick.key) ?? registerPick) : null;
  const registerContent = !panels.register ? null : (
    beforeStart ? (
      <div data-testid="register-tab" style={{ padding: 14, fontSize: 13, opacity: 0.85 }}>デュエル開始前です。開始位置を決めると初手から登録できます（応答タブ）。</div>
    ) : pickingDraw ? (
      // ドローの選び直し: アリーナの横長のデッキ一覧（イラストの帯 + 名前、1 列）。どのカードかは上半分の金の枠
      <div data-testid="register-tab" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div style={{ padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #1c2440', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {playerLabel(pickingDraw.team)} {pickingDraw.label}: <strong>{names[String(pickingDraw.code)] ?? pickingDraw.code}</strong>
        </div>
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <RegisterPanel item={pickingDraw} deck={decks[pickingDraw.team] ?? []} players={players} busy={busy}
            area={{ side: 'bottom', style: { left: 0, right: 0, top: 0, bottom: 0, borderRadius: 0, border: 'none' } }}
            onPick={(code) => { advanceFrom.current = pickingDraw; registerAnswer(pickingDraw, { code: Number(code) }); }}
            onKeep={() => { advanceFrom.current = pickingDraw; confirmItem(pickingDraw); }}
            onClose={() => setRegisterPick(null)} />
        </div>
      </div>
    ) : (
      <div data-testid="register-tab" style={{ padding: 10, height: '100%', overflowY: 'auto', boxSizing: 'border-box', scrollSnapType: 'y mandatory', overscrollBehavior: 'contain', scrollPaddingTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button type="button" data-testid="register-sync-deck" disabled={busy || !replayId} style={btn('plain')} onClick={() => syncDeck()}>デッキレシピを取り直す</button>
        </div>
        <InputPolicyPanel policy={input?.policy ?? null} busy={busy || !replayId} onChange={setPolicy}
          randomPlay={!!input?.random_play} onRandomPlay={setRandomPlay}
          randomPlayLocked={!!roomTo() || (replay?.ops?.length ?? 0) > 0 || (replay?.injections?.length ?? 0) > 0} />
        <RegisterList items={registerItems} names={names} thumbs={thumbs} players={players}
          busy={busy} onPick={setRegisterPick} onConfirm={confirmItem} />
      </div>
    )
  );

  // 発話タブ（Issue #998）: レビューが終わったら講評（試合の行）と発話区間を読み直す
  const onSpeechReviewed = useCallback(async () => {
    speechReview.reload();
    try {
      const info = await api.project('GetDuelGameInfo', { game_uuid: gameUuid });
      if (info?.status === 'success' && info.duel) setGame((g) => ({ ...g, ...info.duel }));
    } catch { /* 講評は次に開いたときに出る */ }
  }, [speechReview.reload, gameUuid]);   // eslint-disable-line react-hooks/exhaustive-deps
  const speechContent = !panels.speech || seatMode ? null : (
    <SpeechTab review={speechReview} activeIndex={speechIdx} game={game} time={time} hasVideo={!!videoEl}
      hasPrev={speechIdx >= 0} hasNext={nextSpeechMs(speech, videoMs) != null}
      onSeek={seekTo} onPrev={() => seekTo(prevSpeechMs(speech, videoMs))} onNext={() => seekTo(nextSpeechMs(speech, videoMs))}
      onPlayPause={togglePlay} onReviewed={onSpeechReviewed} />
  );

  const proposalCount = speech.filter((sg) => sg.proposed_text).length;
  const tabBadge = (key) => {
    if (key === 'register' && unconfirmedCount > 0 && !beforeStart) return unconfirmedCount;
    if (key === 'speech' && proposalCount > 0) return proposalCount;
    if (key === 'respond' && (beforeStart || rview.type !== 'none') && !panels.respond) return '!';
    return null;
  };
  const badgeEl = (badge, style = {}) => (badge != null
    ? <span style={{ minWidth: 18, padding: '0 5px', borderRadius: 999, background: '#e9a045', color: '#1b1308', fontSize: 11, fontWeight: 800, ...style }}>{badge}</span> : null);
  /** タブのボタン。edge はアクティブの線をどちらの辺に引くか（スマホは下端のタブ帯なので上、PC は上端なので下） */
  const tabButton = (t, { edge = 'Top', height = 48 } = {}) => {
    const on = compact ? tab === t.key : panels[t.key];
    return (
      <button key={t.key} type="button" role="tab" aria-selected={on} data-testid={`tab-${t.key}`} onClick={() => switchTab(t.key)} data-keep-playing={t.key === 'speech' ? '1' : undefined}
        style={{ flex: 1, minHeight: height, position: 'relative', background: on ? '#15223d' : 'none', border: 'none', [`border${edge}`]: `3px solid ${on ? '#4fc3ff' : 'transparent'}`,
          color: '#e8eefc', fontSize: 15, fontWeight: on ? 800 : 500, cursor: 'pointer' }}>
        {t.label}
        {badgeEl(tabBadge(t.key), { position: 'absolute', top: 6, marginLeft: 4 })}
      </button>
    );
  };

  const stage = (
    <StageView movieUrl={movieUrl} live={liveVideo} onVideoRef={setVideoEl} videoProps={videoProps} videoSize={fxView ? fxSize : videoSize} zones={fxView ? null : zones}
      board={beforeStart || fxView ? null : (state?.board ?? null)}
      images={thumbs} backs={players.map((p) => p.sleeveImage ?? null)} gray={beforeStart ? new Set() : grayKeys}
      marks={watchMode ? new Set() : stageMarks} focus={watchMode ? null : stageFocus} zoneMarks={watchMode ? new Set() : zoneMarks}
      view={views.includes(view) ? view : 'all'} views={views} onViewChange={setView} showBoard={showBoard && !fxView} onToggleBoard={() => setShowBoard((v) => !v)}
      status={beforeStart || !input?.status ? null : { ...input.status, phaseText: input.texts?.phase ?? null }} life={input?.life ?? [8000, 8000]} players={players}
      flip={flipsView(views.includes(view) ? view : 'all', seatMode ? mySeat : null)} />
  );
  const infoStrip = <InfoStrip ticker={tickerHead} lastText={lastTickerText} subtitles={subtitles} />;
  const pickDialog = registerPick && registerPick.type !== 'draw' ? (
    <RegisterPickDialog key={registerPick.key} item={registerPick} groups={null}
      names={names} thumbs={thumbs} playerLabel={playerLabel} busy={busy}
      onAnswer={(ans) => registerAnswer(registerPick, ans)} onClose={() => setRegisterPick(null)} />
  ) : null;
  const provide = (el) => <PlayerColorsProvider value={playerColors}>{el}</PlayerColorsProvider>;
  const rootStyle = { position: 'fixed', left: 0, top: 0, width: '100%', height: '100dvh', background: '#05060c', color: '#e8eefc', zIndex: 50,
    userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', paddingTop: 'env(safe-area-inset-top)', boxSizing: 'border-box' };

  // 見る専用: 盤面を画面いっぱい + 情報帯 + 再生の最小限（入力の部品は出さない）
  if (watchMode) {
    return provide(
      <div data-testid="ocg-input" data-layout="watch" onContextMenu={(e) => e.preventDefault()} style={{ ...rootStyle, display: 'flex', flexDirection: 'column' }}>
        <div key="stage" style={{ flex: '1 1 0', minHeight: 0 }}>{stage}</div>
        {infoStrip}
        {error ? <div role="alert" style={{ background: '#412', color: '#fcc', padding: '6px 10px', fontSize: 13 }}>{error}</div> : null}
        {liveVideo && !liveVideo.supported ? <div role="alert" data-testid="live-unsupported" style={{ background: '#412', color: '#fcc', padding: '6px 10px', fontSize: 12 }}>この端末のブラウザではライブ映像を再生できません（VideoDecoder が無い）</div> : null}
        {roomOn ? <RoomBar status={roomState} info={roomInfo} canWrite={false} /> : null}
        {noReplay ? <div data-testid="watch-no-replay" style={{ padding: '6px 10px', fontSize: 12, background: '#2a2340' }}>入力がまだ始まっていません（replay ができたら盤面が出ます）</div> : null}
        <WatchBar time={time} hasVideo={!!videoEl} live={live} onBack={() => navigate(`/duels/${duelsId}`)} onPlayPause={togglePlay} onSkip={skipBy} />
      </div>
    );
  }

  // **盤面は並べ方によらず key="stage" の div の中**（React が同じ要素として保ち、動画を作り直さない＝再生位置が頭に戻らない）
  if (!compact) {
    const both = layout === 'both';
    const panelHead = (t) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: 13, fontWeight: 800, borderBottom: '1px solid #1c2440', flexShrink: 0 }}>
        {t.label}{badgeEl(tabBadge(t.key))}
      </div>
    );
    // both の右の列は 登録 / 発話 を切り替える（席モードは登録だけ）
    const sideHead = seatMode ? panelHead(TABS.find((t) => t.key === 'register')) : (
      <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid #1c2440', flexShrink: 0 }}>
        {TABS.filter((t) => t.key === 'register' || t.key === 'speech').map((t) => tabButton(t, { edge: 'Bottom', height: 34 }))}
      </div>
    );
    const panel = (key, body) => (body ? (
      <div key={key} data-testid={`panel-${key}`} data-keep-playing={key === 'speech' ? '1' : undefined}
        style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #1c2440' }}>
        {both ? (key === 'respond' ? panelHead(TABS.find((t) => t.key === key)) : sideHead) : null}
        <div style={{ flex: 1, minHeight: 0 }}>{body}</div>
      </div>
    ) : null);
    return provide(
      <div data-testid="ocg-input" data-layout={layout} onContextMenu={(e) => e.preventDefault()}
        style={{ ...rootStyle, display: 'grid', gridTemplateColumns: `minmax(0, 1fr) ${both ? 'min(960px, 60vw)' : 'min(480px, 45vw)'}`, gridTemplateRows: 'minmax(0, 1fr) auto' }}>
        <div key="stage" style={{ gridColumn: 1, gridRow: 1, minHeight: 0, minWidth: 0 }}>{stage}</div>
        <div key="transport" style={{ gridColumn: 1, gridRow: 2, minWidth: 0 }}>
          {infoStrip}
          {/* 席モードは再生バー（発話区間・再生 / 一時停止）を出さない（Issue #717） */}
          {seatMode ? null : <TransportBar time={time} hasVideo={!!videoEl} live={live} hasPrev={speechIdx >= 0} hasNext={nextSpeechMs(speech, videoMs) != null} lastMs={lastMs}
            onBack={() => navigate(`/duels/${duelsId}`)} onPlayPause={togglePlay} onSeek={seekTo} onSkip={skipBy}
            onPrev={() => seekTo(prevSpeechMs(speech, videoMs))} onNext={() => seekTo(nextSpeechMs(speech, videoMs))} />}
        </div>
        <div key="side" data-testid="input-side" onPointerDownCapture={(e) => { if (!e.target?.closest?.('[data-keep-playing]')) pauseForInput(); }}
          style={{ gridColumn: 2, gridRow: '1 / span 2', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#0a0f1e', paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {noticeBar}
          {both ? null : (
            <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid #1c2440', borderLeft: '1px solid #1c2440', flexShrink: 0 }}>
              {TABS.filter((t) => t.key !== 'play' && (!seatMode || !SEAT_HIDDEN_TABS.has(t.key))).map((t) => tabButton(t, { edge: 'Bottom', height: 40 }))}
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {panel('respond', respondContent)}
            {panel('register', registerContent)}
            {panel('speech', speechContent)}
          </div>
        </div>
        {pickDialog}
      </div>
    );
  }

  return provide(
    <div data-testid="ocg-input" data-layout={layout} onContextMenu={(e) => e.preventDefault()} style={{ ...rootStyle, display: 'flex', flexDirection: 'column' }}>
      <div key="stage" style={{ flex: '0 0 50%', minHeight: 0 }}>{stage}</div>
      <div key="lower" style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', borderTop: '1px solid #1c2440', background: '#0a0f1e' }}>
        {infoStrip}
        {noticeBar}
        <div style={{ flex: 1, minHeight: 0 }}>{playContent ?? respondContent ?? registerContent ?? speechContent}</div>
        <div role="tablist" style={{ display: 'flex', borderTop: '1px solid #1c2440', paddingBottom: 'env(safe-area-inset-bottom)', flexShrink: 0 }}>
          <button type="button" aria-label="戻る" onClick={() => navigate(`/duels/${duelsId}`)} style={{ minHeight: 48, padding: '0 12px', background: 'none', border: 'none', color: '#8896b3', fontSize: 18, cursor: 'pointer' }}>←</button>
          {TABS.filter((t) => !seatMode || !SEAT_HIDDEN_TABS.has(t.key)).map((t) => tabButton(t))}
        </div>
      </div>
      {pickDialog}
    </div>
  );
}

/**
 * ルームにつないでいるときの帯（Issue #765）: つながり（ルーム / 再接続中…）と、奥まったメニュー（⋯）の中に「追いつくまで演出を待たない」と「対戦を終える」。
 * 「対戦を終える」は対戦中いつでも押せる（途中でやめたいときがある）。押し間違えないようメニューの中で、さらに確かめる。決着では自動で閉じない
 */
function RoomBar({ status, info, canWrite, busy = false, onCatchup = null, onClose = null }) {
  const [open, setOpen] = useState(false);
  const retrying = status?.phase !== 'open';
  return (
    <div data-testid="room-bar" data-phase={status?.phase} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', fontSize: 12,
      background: retrying ? '#3a2a14' : '#132a3a', color: retrying ? '#ffd9a0' : '#bfe6ff' }}>
      <span data-testid="room-status" style={{ flex: 1, minWidth: 0 }}>
        {status?.label ?? 'ルーム'}
        {info?.decided ? '（決着。終えるまで記録は続きます）' : ''}
        {info?.catchup ? '・追いつくまで演出を待たない' : ''}
        {info?.closeRequested ? '・対戦を終えています…' : ''}
      </span>
      {canWrite ? (
        <button type="button" data-testid="room-menu" aria-label="ルームのメニュー" aria-expanded={open} onClick={() => setOpen((v) => !v)}
          style={{ background: 'none', border: '1px solid #345', borderRadius: 6, color: 'inherit', padding: '0 8px', minHeight: 26, cursor: 'pointer' }}>⋯</button>
      ) : null}
      {canWrite && open ? (
        <div data-testid="room-menu-items" style={{ position: 'absolute', right: 6, top: '100%', zIndex: 5, display: 'flex', flexDirection: 'column', gap: 6, padding: 8,
          background: '#0f1526', border: '1px solid #345', borderRadius: 8, minWidth: 220 }}>
          <button type="button" data-testid="room-catchup" disabled={busy || retrying} style={btn('plain')}
            onClick={() => { setOpen(false); onCatchup?.(!info?.catchup); }}>{info?.catchup ? '演出を待つに戻す（追いついた）' : '追いつくまで演出を待たない'}</button>
          <button type="button" data-testid="room-close" disabled={busy || retrying || info?.closeRequested} style={btn('danger')}
            onClick={() => { setOpen(false); onClose?.(); }}>対戦を終える</button>
        </div>
      ) : null}
    </div>
  );
}
